/**
 * <amq-popup-links>: opens the AMQ info popups (blocks/amq_product_popups.liquid).
 *
 * Every link on the page whose href contains `#popup-<handle>` opens the dialog
 * `#amq-popup-<handle>` rendered by the block, e.g. the "mehr Info" link in the benefits
 * metafield. Matching links get the info icon from the block's template. Click handling is
 * delegated on the document so links added later (section re-renders) work too. Without this
 * module the links keep their fragment target and nothing happens.
 */
class AmqPopupLinks extends HTMLElement {
  static #selector = 'a[href*="#popup-"]';

  connectedCallback() {
    document.addEventListener('click', this.#onClick);
    this.#decorateLinks();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.#onClick);
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const link = event.target instanceof Element ? event.target.closest(AmqPopupLinks.#selector) : null;
    if (!(link instanceof HTMLAnchorElement)) return;

    const handle = AmqPopupLinks.#handleFrom(link.getAttribute('href') ?? '');
    const dialog = handle ? document.getElementById(`amq-popup-${handle}`) : null;
    if (!dialog) return;

    event.preventDefault();
    customElements
      .whenDefined('dialog-component')
      .then(() => {
        if (typeof (/** @type {any} */ (dialog)).showDialog === 'function') {
          /** @type {any} */ (dialog).showDialog();
        }
      })
      .catch((error) => console.warn('[amq-popup-links]', error));
  };

  /**
   * @param {string} href
   * @returns {string}
   */
  static #handleFrom(href) {
    const index = href.indexOf('#popup-');
    if (index < 0) return '';
    return href
      .slice(index + '#popup-'.length)
      .split(/[?&#/]/)[0]
      .trim();
  }

  #decorateLinks() {
    const template = this.querySelector('template[data-amq-popup-icon]');
    // Only the icon element: the template's whitespace would end up as an underlined space in the link
    const icon = template instanceof HTMLTemplateElement ? template.content.firstElementChild : null;
    for (const link of document.querySelectorAll(AmqPopupLinks.#selector)) {
      if (link.classList.contains('amq-popup-link')) continue;
      const handle = AmqPopupLinks.#handleFrom(link.getAttribute('href') ?? '');
      if (!handle || !document.getElementById(`amq-popup-${handle}`)) continue;

      link.classList.add('amq-popup-link');
      link.setAttribute('aria-haspopup', 'dialog');
      AmqPopupLinks.#moveTrailingSpaceOut(link);
      if (icon) link.append(icon.cloneNode(true));
    }
  }

  /**
   * Link texts in the metafields often end with a space ("mehr Info "), which shows as an
   * underlined gap before the icon. Move it behind the link so the words around stay apart.
   * @param {Element} link
   */
  static #moveTrailingSpaceOut(link) {
    const last = link.lastChild;
    if (!(last instanceof Text)) return;
    const trimmed = last.data.trimEnd();
    if (trimmed === last.data) return;
    last.data = trimmed;
    link.after(' ');
  }
}

if (!customElements.get('amq-popup-links')) {
  customElements.define('amq-popup-links', AmqPopupLinks);
}
